import { detectCardType, type GroupedCardsNode } from './types';

type MdNode = {
  [key: string]: unknown;
  children?: MdNode[];
  type?: string;
  value?: string;
};

const GROUPED_CARDS_OPEN_REGEX = /<div\s+class=["']grouped-cards["']\s*>/iu;
const GROUPED_CARDS_CLOSE_REGEX = /<\/div>/iu;

function getHtmlValue(node: MdNode): string {
  if (node.type === 'html' && typeof node.value === 'string') {
    return node.value;
  }

  if (node.type === 'paragraph' && Array.isArray(node.children)) {
    const htmlChild = node.children.find(
      (child) => child.type === 'html' && typeof child.value === 'string',
    );
    if (htmlChild && typeof htmlChild.value === 'string') {
      return htmlChild.value;
    }
  }

  return '';
}

function isGroupedCardsOpenTag(node: MdNode): boolean {
  return GROUPED_CARDS_OPEN_REGEX.test(getHtmlValue(node));
}

function isGroupedCardsCloseTag(node: MdNode): boolean {
  return GROUPED_CARDS_CLOSE_REGEX.test(getHtmlValue(node));
}

function createParagraphTextNode(text: string): MdNode {
  return {
    type: 'paragraph',
    children: [{ type: 'text', value: text }],
  };
}

function createBlockquoteNode(lines: string[]): MdNode {
  const content = lines
    .map((line) => line.replace(/^>\s*/u, '').trim())
    .join('\n')
    .trim();

  return {
    type: 'blockquote',
    children: content ? [createParagraphTextNode(content)] : [],
  };
}

function createGroupedCardsNode(children: MdNode[]): GroupedCardsNode {
  return {
    type: 'grouped_cards',
    children,
    data: {
      hName: 'div',
      hProperties: {
        className: ['grouped-cards'],
      },
    },
  };
}

function isInlineGroupedCardsNode(node: MdNode): boolean {
  const htmlValue = getHtmlValue(node);
  if (!GROUPED_CARDS_OPEN_REGEX.test(htmlValue)) {
    return false;
  }
  if (!GROUPED_CARDS_CLOSE_REGEX.test(htmlValue)) {
    return false;
  }

  const content = htmlValue
    .replace(GROUPED_CARDS_OPEN_REGEX, '')
    .replace(GROUPED_CARDS_CLOSE_REGEX, '');

  return content.includes('>') || detectCardType(content) !== null;
}

function parseInlineGroupedCards(htmlContent: string): MdNode[] {
  const content = htmlContent
    .replace(GROUPED_CARDS_OPEN_REGEX, '')
    .replace(GROUPED_CARDS_CLOSE_REGEX, '');
  const lines = content.split('\n');

  const groups: string[][] = [];
  let current: string[] = [];

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (trimmed === '') {
      if (current.length > 0) {
        groups.push(current);
        current = [];
      }
      return;
    }

    const startsNew =
      trimmed.startsWith('>') || detectCardType(trimmed) !== null;
    if (startsNew && current.length > 0) {
      groups.push(current);
      current = [];
    }
    current.push(trimmed);
  });

  if (current.length > 0) {
    groups.push(current);
  }

  return groups.map(createBlockquoteNode).filter((node) => {
    const children = Array.isArray(node.children) ? node.children : [];
    return children.length > 0;
  });
}

function transformTree(tree: MdNode): MdNode {
  if (!Array.isArray(tree.children)) {
    return tree;
  }

  const transformed: MdNode[] = [];
  const nodes = tree.children;
  let i = 0;

  while (i < nodes.length) {
    const node = nodes[i];
    if (!node) {
      i += 1;
    } else {
      const inlineCards = isInlineGroupedCardsNode(node)
        ? parseInlineGroupedCards(getHtmlValue(node))
        : [];

      if (inlineCards.length > 0) {
        transformed.push(
          createGroupedCardsNode(inlineCards) as unknown as MdNode,
        );
        i += 1;
      } else if (isGroupedCardsOpenTag(node)) {
        const cardNodes: MdNode[] = [];
        let closeIndex = -1;

        for (let j = i + 1; j < nodes.length; j += 1) {
          const innerNode = nodes[j];
          if (innerNode && isGroupedCardsCloseTag(innerNode)) {
            closeIndex = j;
            break;
          }
          if (innerNode && innerNode.type === 'blockquote') {
            cardNodes.push(innerNode);
          }
        }

        if (closeIndex > i && cardNodes.length > 0) {
          transformed.push(
            createGroupedCardsNode(cardNodes) as unknown as MdNode,
          );
          i = closeIndex + 1;
        } else {
          transformed.push(transformTree(node));
          i += 1;
        }
      } else {
        transformed.push(transformTree(node));
        i += 1;
      }
    }
  }

  return { ...tree, children: transformed };
}

export function remarkGroupedCards() {
  return (tree: MdNode) => transformTree(tree);
}

export default remarkGroupedCards;
